import {
    FC,
    ReactElement,
    useEffect,
} from 'react'
import { useNavigate, useParams } from 'react-router'

import { GENDER } from '@/constants'
import { ProfileDetailForm, ProfileDetailSelectForm } from '@/containers'
import { transformToDashCase } from '@/helpers/text'
import { generateGenderOptions } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { fetchTransportationOptions } from '@/redux/transportationOptions/transportationOptionsActions'
import {
    editGender,
    editTransportationOptions,
    editUserAddress,
    editUserEmail,
    editUserEmergencyContact,
    editUserPhone,
} from '@/redux/user/userActions'

import '@/pages/ProfilePage/ProfilePage.scss'

export const ProfileDetailPage: FC = (): ReactElement | null => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const { detailId: activeSection } = useParams()
    const { currentUser, loading } = useTypedSelector(state => state.userReducer)
    const { transportationOptions } = useTypedSelector(state => state.transportationOptionsReducer)
    const transportationOptionOptions = transportationOptions.map(option => ({
        value: option.id,
        label: option.name,
    }))
    const transportationOptionOptionsWithEmptyValue = transportationOptionOptions.length
        ? [
            { value: '', label: trans('pages.profile.transportationOptionEmpty') },
            ...transportationOptionOptions,
        ]
        : []

    const ADDRESS = trans('pages.profile.address')
    const EMAIL = trans('pages.profile.email')
    const PHONE = trans('pages.profile.phone')
    const EMERGENCY_CONTACT = trans('pages.profile.emergencyContact')
    const GENDER_TRANS = trans('common.gender')
    const TRANSPORTATION_OPTION = trans('pages.profile.transportationOption')

    useEffect(() => {
        if (! transportationOptions.length) {
            dispatch(fetchTransportationOptions())
        }
    }, [])

    if (! currentUser) {
        return null
    }

    const { email, person, phone } = currentUser
    const { emergencyContact } = person
    const transportationOption = currentUser.transportationOptions?.[0]?.id || ''
    const address = person.addresses?.at(-1)

    const onEmailSave = (values: string[]): void => {
        dispatch(editUserEmail(currentUser.id, values[0], navigate))
    }

    const onAddressSave = (values: string[]): void => {
        dispatch(editUserAddress(
            person.id,
            values[0],
            values[1],
            values[2],
            values[3],
            values[4],
            navigate,
        ))
    }

    const onPhoneSave = (values: string[]): void => {
        dispatch(editUserPhone(currentUser.id, values[0], navigate))
    }

    const onEmergencyContactSave = (values: string[]): void => {
        dispatch(editUserEmergencyContact(
            person.id,
            emergencyContact?.id,
            values[0],
            values[1],
            values[2],
            navigate,
        ))
    }

    const onGenderSave = (value: string): void => {
        dispatch(editGender(person.id, value, navigate))
    }

    const onTransportationOptionSave = (value: string): void => {
        const newTransportationOption = transportationOptions.find(o => o.id === value)
        dispatch(
            editTransportationOptions(person.id, newTransportationOption ? [newTransportationOption] : [], navigate),
        )
    }

    if (activeSection === transformToDashCase(EMAIL)) {
        const subject = trans('pages.profile.email').toLowerCase()
        const intro = trans('pages.profile.notifyHRIntro', { item: subject })
        return (
            <ProfileDetailForm
                loading={loading}
                fields={[
                    {
                        propName: EMAIL,
                        value: email,
                        type: 'email',
                    },
                ]}
                title={EMAIL}
                intro={intro}
                onSave={onEmailSave}
            />
        )
    }

    if (activeSection === transformToDashCase(ADDRESS)) {
        const subject = trans('pages.profile.address').toLowerCase()
        const intro = trans('pages.profile.notifyHRIntro', { item: subject })
        return (
            <ProfileDetailForm
                loading={loading}
                fields={[
                    {
                        propName: trans('pages.profile.street'),
                        value: address?.street,
                    },
                    {
                        propName: trans('pages.profile.houseNumber'),
                        value: address?.houseNumber,
                    },
                    {
                        propName: trans('pages.profile.houseNumberAddition'),
                        value: address?.houseNumberAddition ?? undefined,
                    },
                    {
                        propName: trans('pages.profile.postalCode'),
                        value: address?.postalCode,
                    },
                    {
                        propName: trans('pages.profile.city'),
                        value: address?.city,
                    },
                ]}
                title={ADDRESS}
                intro={intro}
                onSave={onAddressSave}
            />
        )
    }

    if (activeSection === transformToDashCase(PHONE)) {
        return (
            <ProfileDetailForm
                loading={loading}
                fields={[
                    {
                        propName: PHONE,
                        value: phone,
                        type: 'tel',
                    },
                ]}
                title={PHONE}
                onSave={onPhoneSave}
            />
        )
    }

    if (activeSection === transformToDashCase(EMERGENCY_CONTACT)) {
        return (
            <ProfileDetailForm
                loading={loading}
                fields={[
                    {
                        propName: trans('common.name'),
                        value: emergencyContact?.fullName,
                    },
                    {
                        propName: PHONE,
                        value: emergencyContact?.phone,
                        type: 'tel',
                    },
                    {
                        propName: trans('pages.profile.relationWithEmergencyPerson'),
                        value: emergencyContact?.relationType,
                    },
                ]}
                title={EMERGENCY_CONTACT}
                onSave={onEmergencyContactSave}
            />
        )
    }

    if (activeSection === transformToDashCase(GENDER_TRANS)) {
        return (
            <ProfileDetailSelectForm
                loading={loading}
                propName={GENDER}
                value={person.gender}
                options={generateGenderOptions(person.gender)}
                title={trans('common.gender')}
                onSave={onGenderSave}
            />
        )
    }

    if (activeSection === transformToDashCase(TRANSPORTATION_OPTION)) {
        return (
            <ProfileDetailSelectForm
                loading={loading || ! transportationOptionOptionsWithEmptyValue.length}
                propName=""
                value={transportationOption}
                options={transportationOptionOptionsWithEmptyValue}
                title={trans('pages.profile.transportationOption')}
                onSave={onTransportationOptionSave}
            />
        )
    }

    return null
}
